// Hermes use case — FanOutAndMerge: the fan-out + merge driver (D14).
//
// The parent of a fan-out run is a COORDINATOR: it never runs an LLM loop
// itself. Lifecycle it drives:
//
//   startRun (queued) → claim (running, CAS) → POST fanout (parent
//   fanning_out + N children queued) → drive each child via driveRun →
//   all children terminal → DOMAIN merge invariant (createRun().beginMerge)
//   → PATCH merging → deterministic merge text → PATCH done.
//
// Concurrency is gated on ISOLATION: children run in PARALLEL only when the
// allocator gave each one its own worktree root; otherwise they share the
// parent root and run SEQUENTIALLY (zero clobber risk). Isolation failing is
// never an error — it just downgrades to the serial path.
//
// fanout error = STATE UNKNOWN: a mid-insert crash can leave a fanning_out
// parent with PARTIAL children. On any fanout failure we re-GET the run and
// reconcile — if the parent is fanning_out with children, those children are
// real: drive them; otherwise cancel the parent and rethrow.
//
// Injected ports only (Dependency Rule — no infrastructure imports):
//   api       — hermesApiClient shape (startRun/getRun/patchRunState/
//               cancelRun/fanOut); non-2xx throws carry `.status`
//   executor  — ExecutionPort (only used to build the default driveRun)
//   store     — live sink: setLive/appendEvent
//   clock     — () => ms epoch
//   allocator — WorktreeAllocator port: allocate(parentRoot, runId, count) →
//               { isolated, roots, allocations? }; release(allocations)
//   driveRun  — injectable child driver (tests); defaults to makeRunAgent's
//   merge     — injectable merge strategy (children, results) → string|Promise.
//               Defaults to the deterministic mergeChildResults; Team Mode
//               injects the LLM synthesis here (the D8 deferred upgrade). If an
//               injected merge THROWS, we fall back to the deterministic one —
//               a merge strategy can never fail a fan-out that already ran.

import { createRun } from '../domain/index.js';
import { makeRunAgent, makePatchFinal } from './runAgent.js';
import { mergeChildResults } from './mergeChildResults.js';
import { settleWithConcurrency, FANOUT_MAX_CONCURRENCY } from './boundedSettle.js';

// G4-WS1-T02 — the coordinator's phases (fan-out+reconcile, drive-children, the merge
// invariant, the merging CAS, the merge-text resolution, isolation allocation) were all
// inline in one cognitive-51 / complexity-59 function inside a 241-line factory. Each
// phase is now a module-private helper taking explicit deps; makeFanOutAndMerge closes
// over api/store/clock/… and fanOutAndMerge is a thin orchestrator. Byte-identical: same
// branches, same order, same returns, same best-effort swallows — fanOutAndMerge.test 40
// (with runTeamTurn) unchanged.

// Normalize a server-side child record into the merge result shape — used when WE didn't
// produce the result (lost claim → another executor owns it).
function recordToResult(rec) {
  if (rec?.state === 'done') return { ok: true, finalText: rec.result ?? '' };
  if (rec?.state === 'cancelled') {
    return { ok: false, stopReason: 'aborted', finalText: rec.result ?? '' };
  }
  return { ok: false, error: rec?.error || rec?.state || 'unknown', finalText: rec?.result ?? '' };
}

// One settled drive outcome → { child, result } for the merge. A rejection is synthesized
// as a failure; in BOTH the rejection and lost-claim cases the child record is refetched —
// the server is the only source of truth for child state the UI can rely on.
async function resolveChildRecord(api, child, settled) {
  if (settled.status === 'rejected') {
    let rec = child;
    try {
      const detail = await api.getRun(child.id);
      if (detail?.run) rec = detail.run;
    } catch {
      /* keep the last-known record */
    }
    if (rec?.state === 'done' || rec?.state === 'cancelled') {
      return { child: rec, result: recordToResult(rec) };
    }
    const derived = recordToResult(rec);
    return { child: rec, result: { ...derived, error: derived.error || String(settled.reason) } };
  }
  const out = settled.value;
  if (out?.claimed === false) {
    let rec = out.run ?? child;
    try {
      const detail = await api.getRun(child.id);
      if (detail?.run) rec = detail.run;
    } catch {
      /* keep the last-known record */
    }
    return { child: rec, result: recordToResult(rec) };
  }
  return {
    child: out?.run ?? child,
    result: out?.result ?? { ok: false, error: 'driver returned no result' },
  };
}

// Phase 2 — isolation. The allocator degrades on its own; if it THROWS we degrade for it
// (isolation is an optimization, never a blocker). Returns { alloc, isolated }.
async function allocateIsolation(allocator, run, count) {
  let alloc = null;
  try {
    alloc = await allocator.allocate(run.root, run.id, count);
  } catch (e) {
    console.warn('[hermes] allocator threw — falling back to serial-shared root:', e);
    alloc = null;
  }
  const isolated = !!(alloc?.isolated && Array.isArray(alloc.roots) && alloc.roots.length === count);
  return { alloc, isolated };
}

// Phase 3-4 — fan out. Non-201 = STATE UNKNOWN: re-GET and reconcile; if the parent is
// fanning_out with children, drive what exists, else cancel best-effort + rethrow.
async function fanOutChildren(api, run, payload) {
  try {
    return (await api.fanOut(run.id, payload)).children;
  } catch (err) {
    let detail = null;
    try {
      detail = await api.getRun(run.id);
    } catch {
      detail = null;
    }
    if (detail?.run?.state === 'fanning_out' && Array.isArray(detail.children) && detail.children.length > 0) {
      return detail.children; // the insert DID land — drive what exists
    }
    try {
      await api.cancelRun(run.id); // don't leave a zombie parent behind
    } catch {
      /* best-effort */
    }
    throw err;
  }
}

// Phase 5 — drive the children. PARALLEL only when isolated, but BOUNDED
// (settleWithConcurrency: same format/order, max FANOUT_MAX_CONCURRENCY at once — Onda 2
// anti-travamento). Returns the resolved [{ child, result }] aligned with `children`.
async function driveAllChildren({ children, isolated, drive, agent, signal, store, run, resolveChild }) {
  const resolved = [];
  if (isolated) {
    const settled = await settleWithConcurrency(children, FANOUT_MAX_CONCURRENCY, (c) => drive(c, agent, { signal }));
    for (let i = 0; i < children.length; i++) {
      const rc = await resolveChild(children[i], settled[i]);
      resolved.push(rc);
      store.appendEvent(run.id, { type: 'child_done', childId: children[i].id, index: i, ok: !!rc.result?.ok });
    }
    return resolved;
  }
  for (let i = 0; i < children.length; i++) {
    const c = children[i];
    if (signal?.aborted) {
      // Short-circuit: cancelRun's cascade covers the queued remainder. Keep `results`
      // aligned with `children` for the return shape.
      resolved.push({ child: c, result: { ok: false, stopReason: 'aborted' } });
      continue;
    }
    let settled;
    try {
      settled = { status: 'fulfilled', value: await drive(c, agent, { signal }) };
    } catch (reason) {
      settled = { status: 'rejected', reason };
    }
    const rc = await resolveChild(c, settled);
    resolved.push(rc);
    store.appendEvent(run.id, { type: 'child_done', childId: c.id, index: i, ok: !!rc.result?.ok });
  }
  return resolved;
}

// Phase 6 — the DOMAIN owns the merge invariant: beginMerge throws children_pending if
// ANY child is still non-terminal. Returns { detail } on success, else { detail, failed }
// (the failed-parent result the caller returns).
async function assertMergeInvariant({ api, run, patchFinal, clock, finalChildren, results }) {
  const detail = await api.getRun(run.id);
  try {
    createRun(detail.run).beginMerge({ childStates: (detail.children ?? []).map((c) => c.state) });
  } catch (e) {
    // Reachable: a child claimed by ANOTHER live executor may still be non-terminal. We
    // refuse to merge pending children (the invariant) and fail the parent.
    const fin = await patchFinal(run.id, {
      state: 'failed',
      error: 'children pending after drive',
      finishedAt: clock(),
    }).catch(() => ({ run: null, stale: true }));
    return {
      detail,
      failed: { claimed: true, failed: true, error: e?.message || String(e), run: fin.run ?? detail.run, children: finalChildren, results },
    };
  }
  return { detail };
}

// Phase 7 — PATCH merging (server-side CAS; a 409 means a cancel won). Returns
// { merging } or { cancelled } (the cancelled-parent result the caller returns).
async function claimMerging({ api, run, detail, finalChildren, results, store }) {
  store.appendEvent(run.id, { type: 'merge_started' });
  try {
    return { merging: await api.patchRunState(run.id, { state: 'merging' }) };
  } catch (e) {
    if (e?.status !== 409) throw e;
    let rec = detail?.run ?? run;
    try {
      rec = (await api.getRun(run.id))?.run ?? rec;
    } catch {
      /* keep the last-known record */
    }
    return { cancelled: { claimed: true, cancelled: true, run: rec, children: finalChildren, results } };
  }
}

// Phase 8 — merge → text. Custom strategy first (Team Mode synthesis); any throw (or a
// non-string / blank) degrades to the deterministic merge — never fails the run.
async function resolveMergedText(merge, finalChildren, results) {
  if (!merge) return mergeChildResults(finalChildren, results);
  let merged;
  try {
    merged = await merge(finalChildren, results);
  } catch (e) {
    console.warn('[hermes] injected merge threw — deterministic fallback:', e);
    merged = null;
  }
  if (typeof merged !== 'string' || !merged.trim()) return mergeChildResults(finalChildren, results);
  return merged;
}

// Phase 1 — enqueue + claim (CAS; a 409 means another executor won — normal). onStarted
// fires right after startRun (BEFORE claim/fanout) so the UI learns the PARENT id early
// (T08). Returns { run } (claimed) or { claimed:false, run } (lost the race).
async function startAndClaimParent({ api, agent, task, clock, onStarted }) {
  const run = await api.startRun(agent.id, task);
  try {
    onStarted?.(run);
  } catch {
    /* best-effort UI hook */
  }
  try {
    await api.patchRunState(run.id, { state: 'running', startedAt: clock() });
  } catch (e) {
    if (e?.status === 409) return { claimed: false, run };
    throw e;
  }
  return { run };
}

// Phase 10 — abort beats merge: every child is finalized (driveRun saw the same signal) —
// cancel the parent (cascades leftovers), never merge.
async function cancelParentOnAbort(api, run, finalChildren, results) {
  let cancelled = null;
  try {
    cancelled = await api.cancelRun(run.id);
  } catch {
    /* the server may already be terminal — its record stands */
  }
  return { claimed: true, cancelled: true, run: cancelled?.run ?? run, children: finalChildren, results };
}

// Phase 9 — give the worktrees back (release is contractually non-throwing; stay
// defensive anyway).
async function releaseAllocation(allocator, alloc) {
  if (Array.isArray(alloc?.allocations) && alloc.allocations.length > 0) {
    try {
      await allocator.release(alloc.allocations);
    } catch {
      /* release is contractually non-throwing; stay defensive anyway */
    }
  }
}

export function makeFanOutAndMerge({
  api,
  executor,
  store,
  clock = Date.now,
  allocator,
  driveRun,
  appendMemory,
  merge,
} = {}) {
  // appendMemory threads through to the child driver so fan-out children auto-append a
  // run-summary memory on success exactly like single runs (D18).
  const drive = driveRun ?? makeRunAgent({ api, executor, store, clock, appendMemory }).driveRun;
  // Finalize PATCHes share runAgent's 409-refetch contract — ONE owner (makePatchFinal)
  // so the swallow/refetch rules can't drift between the single-run driver and this one.
  const patchFinal = makePatchFinal(api);
  const resolveChild = (child, settled) => resolveChildRecord(api, child, settled);

  /**
   * Fan a task out into `subtasks` (strings) under `agent` and merge.
   * Returns one of:
   *   { claimed:false, run }                       — lost the claim race
   *   { claimed:true, cancelled:true, run, children, results }
   *       — aborted mid-drive OR a cancel won the merging CAS
   *   { claimed:true, failed:true, error, run, children, results }
   *       — defensive: the domain refused beginMerge (children still pending)
   *   { claimed:true, run, children, results, merged [, stale:true] }
   *       — merged + done (stale only when the final refetch failed)
   * Throws when the fanout failed AND reconcile found no children (the parent is
   * cancelled best-effort first).
   */
  async function fanOutAndMerge(agent, task, subtasks, { signal, onStarted } = {}) {
    // 0. Cheap guard BEFORE creating a parent run we'd only have to cancel.
    if (!Array.isArray(subtasks) || subtasks.length === 0) {
      throw new Error('subtasks required');
    }
    // 1. Enqueue + claim. A lost claim (409) short-circuits before setLive/allocation.
    const claim = await startAndClaimParent({ api, agent, task, clock, onStarted });
    if (claim.claimed === false) return claim;
    const run = claim.run;

    store.setLive(run.id, true);
    let alloc = null;
    try {
      const iso = await allocateIsolation(allocator, run, subtasks.length);
      alloc = iso.alloc;
      // 3-4. Fan out. Isolated → per-child worktree roots; otherwise plain strings
      //      (children inherit the parent root server-side).
      const payload = iso.isolated
        ? subtasks.map((s, i) => ({ task: s, root: alloc.roots[i] }))
        : [...subtasks];
      const children = await fanOutChildren(api, run, payload);
      store.appendEvent(run.id, { type: 'fanout_started', count: children.length });

      const resolved = await driveAllChildren({ children, isolated: iso.isolated, drive, agent, signal, store, run, resolveChild });
      const finalChildren = resolved.map((r) => r.child);
      const results = resolved.map((r) => r.result);

      if (signal?.aborted) return await cancelParentOnAbort(api, run, finalChildren, results);

      const inv = await assertMergeInvariant({ api, run, patchFinal, clock, finalChildren, results });
      if (inv.failed) return inv.failed;

      const cm = await claimMerging({ api, run, detail: inv.detail, finalChildren, results, store });
      if (cm.cancelled) return cm.cancelled;

      // 8. Merge → done. Custom strategy first (Team Mode synthesis); any throw degrades
      //    to the deterministic merge — never fails the run.
      const merged = await resolveMergedText(merge, finalChildren, results);
      const fin = await patchFinal(run.id, { state: 'done', result: merged, finishedAt: clock() });
      store.appendEvent(run.id, {
        type: 'merge_done',
        okCount: results.filter((r) => r?.ok).length,
        total: results.length,
      });
      const out = { claimed: true, run: fin.run ?? cm.merging, children: finalChildren, results, merged };
      if (!fin.run && fin.stale) out.stale = true;
      return out;
    } finally {
      // 9. ALWAYS: give the worktrees back + clear the parent's live flag (children's
      //    flags are driveRun's job).
      await releaseAllocation(allocator, alloc);
      store.setLive(run.id, false);
    }
  }

  return { fanOutAndMerge };
}
